// ============================================================
// MCB Document Automation Agent — Live Dashboard
// Connects to FastAPI backend at same origin
// Brand: Deep navy (#1B2A4A), Gold (#C6993E), Warm whites
// ============================================================

// Only the imports the App component itself uses. Page-level
// components live in pages/, chrome lives in components/, matter-
// detail tab cards live in matter-detail/. App's job is hash-
// routing the top-level page + rendering the chrome.

import React, { useCallback, useState, useEffect, useRef } from "react";
import { C } from "./constants.js";
import { I } from "./icons.jsx";
import { apiFetch, setSessionExpiredHandler } from "./api.js";
import { Spinner } from "./components/Spinner.jsx";
import { LoginPage } from "./components/LoginPage.jsx";
import { ConnectionBanner } from "./components/ConnectionBanner.jsx";
// NotificationBell + RoleIndicator retired from the top-level render in
// Substep 5-η.6: bell moved into TopBar; RoleIndicator superseded by
// the user pill in TopBar.
import { Sidebar } from "./components/Sidebar.jsx";
import { TopBar, TOP_BAR_HEIGHT } from "./components/TopBar.jsx";
import { HomePage } from "./pages/HomePage.jsx";
import { MattersPage } from "./pages/MattersPage.jsx";
import { ConflictCheckPage } from "./pages/ConflictCheckPage.jsx";
import { DataSyncPage } from "./pages/DataSyncPage.jsx";
import { FirmFeedPage } from "./pages/FirmFeedPage.jsx";
import { MyDayPage } from "./pages/MyDayPage.jsx";
import { ReportsPage } from "./pages/ReportsPage.jsx";
import { FinancePage } from "./pages/FinancePage.jsx";
import { StaleBuildNotice } from "./components/StaleBuildNotice.jsx";
import { DeadlinesPage } from "./pages/DeadlinesPage.jsx";
import { StatuteGapsPage } from "./pages/StatuteGapsPage.jsx";
import { DeadlinesHubPage } from "./pages/DeadlinesHubPage.jsx";
import { SettingsHubPage } from "./pages/SettingsHubPage.jsx";
import { PlatformHubPage } from "./pages/PlatformHubPage.jsx";
import { useIsMobile } from "./useIsMobile.js";
import { NewMatterPage } from "./pages/NewMatterPage.jsx";
import { TemplatesPage } from "./pages/TemplatesPage.jsx";
import { GeneratePage } from "./pages/GeneratePage.jsx";
import { AuditLogPage } from "./pages/AuditLogPage.jsx";
import { FolderUploadPage } from "./pages/FolderUploadPage.jsx";
import { GenerationHistoryPage } from "./pages/GenerationHistoryPage.jsx";
import { ExtractionReviewPage } from "./pages/ExtractionReviewPage.jsx";
import { PipelineDashboardPage } from "./pages/PipelineDashboardPage.jsx";
import { WebhooksAdminPage } from "./pages/WebhooksAdminPage.jsx";
import { TemplateSweepPage } from "./pages/TemplateSweepPage.jsx";
import { UsersAdminPage } from "./pages/UsersAdminPage.jsx";
import { MatterDetailPage } from "./pages/MatterDetailPage.jsx";
import { FirmSettingsPage } from "./pages/FirmSettingsPage.jsx";
import { MyProfilePage } from "./pages/MyProfilePage.jsx";
import { PlatformFirmsPage } from "./pages/PlatformFirmsPage.jsx";
import { FirmDirectoryPage } from "./pages/FirmDirectoryPage.jsx";
import { FirmSopsPage } from "./pages/FirmSopsPage.jsx";
import { TaskTemplatesPage } from "./pages/TaskTemplatesPage.jsx";
import { PlatformDirectoryPage } from "./pages/PlatformDirectoryPage.jsx";

// ============================================================
// HASH ROUTING
// ============================================================
// Persist the current page + matter in the URL hash so a refresh returns
// the user to where they were. Format:
//   #/home | #/matters | #/matters/{id} | #/new-matter | #/templates |
//   #/generate | #/upload-folder | #/gen-history | #/review-queue |
//   #/audit-log
function _parseHash() {
  const raw = (window.location.hash || "").replace(/^#\/?/, "");
  const [first, second] = raw.split("/");
  const key = first || "home";
  if (key === "matters" && second) {
    const id = Number(second);
    if (Number.isFinite(id)) return { page: "matter", matterId: id };
  }
  return { page: key, matterId: null };
}

function _hashFor(page, matter) {
  if (page === "matter" && matter?.id) return `#/matters/${matter.id}`;
  return `#/${page}`;
}

// ============================================================
// MAIN APP
// ============================================================
function App() {
  const _initial = _parseHash();
  const [page, setPage] = useState(_initial.page);
  const [selectedMatter, setSelectedMatter] = useState(
    _initial.matterId ? { id: _initial.matterId } : null,
  );
  const [authStatus, setAuthStatus] = useState(null);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Current firm — brand strings, display names, colors. Loaded from
  // /api/current-firm (public endpoint) once at app boot. Passed as a
  // prop to any component that displays firm-branded copy.
  // refreshCurrentFirm re-fetches; passed to FirmSettingsPage so a
  // save propagates to the sidebar/login/home without a page reload.
  const [currentFirm, setCurrentFirm] = useState(null);
  const refreshCurrentFirm = () => {
    apiFetch("/api/current-firm")
      .then(setCurrentFirm)
      .catch(e => console.warn("current-firm load failed:", e));
  };

  // Sidebar collapse — persisted across reloads so each user gets to
  // pick their preferred mode. Width swap: 240 ↔ 64 px.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem("mcb_sidebar_collapsed") === "1";
    } catch { return false; }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed(v => {
      const next = !v;
      try { window.localStorage.setItem("mcb_sidebar_collapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  };
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);
  // The drawer overlays the content, so it must not also reserve space.
  const SIDEBAR_WIDTH = isMobile ? 0 : (sidebarCollapsed ? 64 : 240);

  useEffect(() => {
    apiFetch("/auth/status")
      .then(data => { setAuthStatus(data); setLoading(false); })
      .catch(() => { setAuthStatus({ authenticated: false }); setLoading(false); });

    // Firm brand — public endpoint, works pre-login. Silent failure
    // falls back to hardcoded copy in child components so the app
    // still renders if firm resolution is temporarily broken.
    refreshCurrentFirm();

    // Register global 401 handler. apiFetch calls this on any 401
    // response so the UI can surface the "Clio sign-in required"
    // modal without every fetch site having to handle the redirect.
    setSessionExpiredHandler(() => {
      setSessionExpired(true);
      setAuthStatus({ authenticated: false });
    });
    return () => { setSessionExpiredHandler(null); };
  }, []);

  // Sync page/selectedMatter -> URL hash (replaceState so we don't flood history)
  useEffect(() => {
    const target = _hashFor(page, selectedMatter);
    if (window.location.hash !== target) {
      window.history.replaceState(null, "", target);
    }
  }, [page, selectedMatter]);

  // Respond to browser back/forward
  useEffect(() => {
    const onHashChange = () => {
      const parsed = _parseHash();
      setPage(parsed.page);
      setSelectedMatter(prev => {
        if (parsed.matterId) {
          return prev?.id === parsed.matterId ? prev : { id: parsed.matterId };
        }
        return null;
      });
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // These MUST stay referentially stable.
  //
  // Ten components take `showNotification` in a useCallback dependency
  // array for a loader that ALSO calls showNotification when the fetch
  // fails. As a plain inline function it got a new identity on every
  // App render, which closed a loop the moment any request errored:
  //
  //   load fails → showNotification → setNotification → App re-renders
  //   → new showNotification identity → new load identity
  //   → useEffect([load]) re-runs → load fails → …
  //
  // Unbounded, and every mounted card runs it at once. On 2026-08-17 a
  // backend hiccup on a matter page turned into ~22,000 requests, the
  // browser hit ERR_INSUFFICIENT_RESOURCES, and Railway started
  // returning 502s — the client took production down. Reproduced at
  // ~2,500 requests/second with every endpoint stubbed to 500.
  //
  // useCallback([]) is correct here: both close over setState functions
  // and a ref, all of which React guarantees are stable.
  const notificationTimerRef = useRef(null);
  const showNotification = useCallback((message, type = "success") => {
    if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current);
    setNotification({ message, type });
    if (type === "success") {
      notificationTimerRef.current = setTimeout(() => setNotification(null), 4000);
    }
  }, []);

  const navigateToMatter = useCallback((matter) => {
    setSelectedMatter(matter);
    setPage("matter");
  }, []);

  if (loading) {
    return React.createElement("div", {
      style: { minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.cream }
    }, React.createElement(Spinner, { size: 40 }));
  }

  if (!authStatus?.authenticated && !sessionExpired) {
    return React.createElement(LoginPage, { currentFirm });
  }

  if (sessionExpired) {
    return React.createElement("div", { style: { minHeight:"100vh", background:C.cream, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" } },
      React.createElement("div", {
        style: {
          background:"#fff", borderRadius:"16px", padding:"48px", maxWidth:"440px", textAlign:"center",
          boxShadow:"0 4px 24px rgba(0,0,0,0.08)", border:`1px solid ${C.warmGray}`,
        }
      },
        React.createElement("div", { style: { fontSize:"48px", marginBottom:"16px" } }, "\u{1F511}"),
        React.createElement("h2", { style: { fontFamily:"'Playfair Display',serif", fontSize:"22px", color:C.navy, marginBottom:"8px" } }, "Clio sign-in required"),
        React.createElement("p", { style: { color:C.textMuted, fontSize:"14px", lineHeight:"1.6", marginBottom:"24px" } },
          "Your Clio connection couldn't be refreshed. This usually means it's been over 90 days since your last login, your Clio password changed, or the connection was revoked. Click below to sign back in \u2014 your work in Clio is unaffected."
        ),
        React.createElement("a", {
          href: "/auth/login",
          style: {
            display:"inline-block", padding:"12px 32px", borderRadius:"10px",
            background:C.gold, color:"#fff", fontSize:"15px", fontWeight:700,
            textDecoration:"none", boxShadow:"0 2px 8px rgba(198,153,62,0.3)",
          },
        }, "Sign Back In"),
      ),
    );
  }

  return React.createElement("div", { style: { minHeight:"100vh", background:C.cream } },
    // Notification Toast
    notification && React.createElement("div", {
      className: "slide-down",
      style: {
        position:"fixed", top:20, right:20, zIndex:1000, maxWidth:"480px",
        padding:"14px 20px", borderRadius:"10px",
        background: notification.type === "success" ? C.success : notification.type === "warning" ? C.goldDim : C.error,
        color:"#fff", fontSize:"14px", fontWeight:500,
        boxShadow:"0 8px 32px rgba(0,0,0,0.2)",
        display:"flex", alignItems:"center", gap:"8px",
      }
    },
      notification.type === "success" ? I.check : I.alert,
      React.createElement("span", { style: { flex:1 } }, notification.message),
      React.createElement("button", {
        onClick: () => setNotification(null),
        style: {
          border:"none", background:"none", color:"#fff", cursor:"pointer",
          fontSize:"16px", fontWeight:700, opacity:0.8, marginLeft:"12px", padding:"0 2px", lineHeight:1,
        }
      }, "\u00d7"),
    ),

    // Sidebar — collapsed state owned by App so the main content
    // margin can sync to the same width swap.
    React.createElement(Sidebar, {
      page, setPage: (p) => {
        setPage(p);
        // Navigating closes the drawer. Leaving it open would hide the
        // page the user just asked for behind the thing they used to
        // ask for it.
        setNavOpen(false);
      },
      authStatus, currentFirm,
      collapsed: sidebarCollapsed,
      onToggle: toggleSidebar,
      isMobile, open: navOpen, onClose: () => setNavOpen(false),
    }),

    // Substep 5-η.6: TopBar carries Clio-connection status + user menu
    // (Profile / Sign Out) + notification bell. Sidebar footer used to
    // own the identity bits; the floating RoleIndicator is gone
    // (redundant with the pill inside TopBar).
    React.createElement(TopBar, {
      authStatus, sidebarWidth: SIDEBAR_WIDTH, setPage,
      isMobile, onOpenNav: () => setNavOpen(true),
      onNavigateToMatter: navigateToMatter,
    }),

    // Connection Health Banner
    // Main Content — left margin tracks the sidebar width so the
    // content reflows when the user collapses the nav. Top padding
    // matches TopBar height so content isn't hidden underneath.
    React.createElement("div", {
      style: {
        marginLeft: `${SIDEBAR_WIDTH}px`,
        // The top bar grows by the notch inset on mobile, so the
        // content has to clear both or the first card sits under it.
        paddingTop: isMobile
          ? `calc(${TOP_BAR_HEIGHT}px + env(safe-area-inset-top))`
          : `${TOP_BAR_HEIGHT}px`,
        paddingBottom: isMobile ? "env(safe-area-inset-bottom)" : 0,
        minHeight: "100vh",
        transition: "margin-left 0.18s ease",
      },
    },
      // First child of the content area, in normal flow rather than
      // fixed over it.
      //
      // Fixed, it sat on top of whatever was at the top of the page:
      // anchored at top:0 it covered the TopBar, and moved below the
      // bar it covered the page heading instead — it hid "Firm SOPs"
      // entirely. Neither is acceptable for a banner that stays up
      // until dismissed.
      //
      // In flow it pushes the page down by exactly its own height,
      // which also removes any need to know that height elsewhere.
      React.createElement(StaleBuildNotice, null),
      React.createElement(ConnectionBanner, null),

      // "home" is the personal landing page now; the firm-wide
      // dashboard it replaced still exists at "firm-overview".
      page === "home" && React.createElement(MyDayPage, { authStatus, onNavigate: setPage, onMatterClick: navigateToMatter }),
      page === "reports" && React.createElement(ReportsPage, { onMatterClick: navigateToMatter }),
      page === "finance" && React.createElement(FinancePage, { section: "costs", onMatterClick: navigateToMatter }),
      page === "finance-revenue" && React.createElement(FinancePage, { section: "revenue", onMatterClick: navigateToMatter }),
      page === "firm-overview" && React.createElement(HomePage, { onNavigate: setPage, onMatterClick: navigateToMatter, authStatus, currentFirm }),
      page === "matters" && React.createElement(MattersPage, { onMatterClick: navigateToMatter }),
      page === "conflicts" && React.createElement(ConflictCheckPage, null),
      page === "data-sync" && React.createElement(DataSyncPage, null),
      page === "firm-feed" && React.createElement(FirmFeedPage, { onMatterClick: navigateToMatter }),
      // Kept as an alias so existing links to #/today still resolve —
      // it lands on the same page with the firm scope selected.
      page === "today" && React.createElement(MyDayPage, {
        authStatus, onNavigate: setPage, onMatterClick: navigateToMatter,
        initialScope: "firm",
      }),
      // Upcoming + "not yet tracked" in one place. The second used
      // to be its own nav row with the same clock icon.
      page === "deadlines" && React.createElement(DeadlinesHubPage, {
        onMatterClick: navigateToMatter, showNotification }),
      page === "statute-gaps" && React.createElement(StatuteGapsPage, {
        onMatterClick: navigateToMatter, showNotification }),
      page === "matter" && selectedMatter && React.createElement(MatterDetailPage, {
        matter: selectedMatter, onBack: () => setPage("matters"), showNotification, setPage, authStatus
      }),
      page === "new-matter" && React.createElement(NewMatterPage, {
        onMatterClick: navigateToMatter, showNotification
      }),
      page === "pipeline" && React.createElement(PipelineDashboardPage, {
        onMatterClick: navigateToMatter, showNotification
      }),
      page === "templates" && React.createElement(TemplatesPage, null),
      // initialMatter: the matter page links here with the matter you
      // were already on, so the ad-hoc generator opens preselected.
      page === "generate" && React.createElement(GeneratePage, {
        onMatterClick: navigateToMatter, initialMatter: selectedMatter, showNotification,
      }),
      page === "upload-folder" && React.createElement(FolderUploadPage, { showNotification }),
      page === "gen-history" && React.createElement(GenerationHistoryPage, { onMatterClick: navigateToMatter }),
      page === "review-queue" && React.createElement(ExtractionReviewPage, { showNotification }),
      page === "settings" && React.createElement(SettingsHubPage, {
        authStatus, currentFirm, onFirmChange: refreshCurrentFirm,
        showNotification, onMatterClick: navigateToMatter }),
      page === "platform" && React.createElement(PlatformHubPage, {
        authStatus, showNotification }),

      // Kept reachable by id. Most are only entered through Settings
      // now, but audit-log is linked from the matter Activity card
      // and Firm Overview, and a dead route is worse than a spare one.
      page === "audit-log" && React.createElement(AuditLogPage, { onMatterClick: navigateToMatter }),
      page === "users" && React.createElement(UsersAdminPage, { showNotification, authStatus }),
      page === "admin-webhooks" && React.createElement(WebhooksAdminPage, { showNotification }),
      page === "admin-template-sweep" && React.createElement(TemplateSweepPage, { showNotification }),
      page === "firm-settings" && React.createElement(FirmSettingsPage, {
        authStatus, currentFirm, onFirmChange: refreshCurrentFirm, showNotification,
      }),
      page === "my-profile" && React.createElement(MyProfilePage, {
        authStatus, showNotification,
      }),
      page === "platform-firms" && React.createElement(PlatformFirmsPage, {
        authStatus, showNotification,
      }),
      page === "firm-directory" && React.createElement(FirmDirectoryPage, {
        authStatus, showNotification,
      }),
      page === "firm-sops" && React.createElement(FirmSopsPage, {
        authStatus, showNotification,
      }),
      page === "task-templates" && React.createElement(TaskTemplatesPage, {
        showNotification,
      }),
      page === "platform-directory" && React.createElement(PlatformDirectoryPage, {
        showNotification,
      }),
    )
  );
}

export default App;
